import { useCallback, useEffect, useState } from 'react';
import { Button } from 'reactstrap';
import { useDictation } from '../InstructionAssistant/useDictation';
import MicDropStatus from '../../api/MicDropStatus';

// Same key Recorder.js persists its mic choice under, so the Live Visit and
// Dictation pickers agree on the device (read on mount and each time the menu
// opens, rather than live-syncing).
const MIC_STORAGE_KEY = 'comprehendpt_selectedMicId';

function readStoredMicId() {
  try {
    return localStorage.getItem(MIC_STORAGE_KEY) || '';
  } catch (e) {
    return '';
  }
}

// Shortened for the two-line button face, where there's room for a word or two.
function micButtonLabel(label) {
  const clean = (label || '').replace(/^Default - /, '').trim();
  if (!clean) {
    return 'Mic';
  }
  if (/\b(built[- ]?in|internal|integrated|microphone array)\b/i.test(clean) ||
    /\bmacbook\b/i.test(clean)
  ) {
    return 'Built-in Mic';
  }
  return clean.replace(/\bmicrophone\b/gi, 'Mic');
}

/**
 * Push-to-talk dictation button that sits beside the Live Visit button, with a
 * matching chevron for picking a microphone.
 *
 * Records a clip, transcribes it, and hands the text to `onText` — the views
 * append it to the Clinical Facts field. Strictly additive: typing into that
 * field always works, and a failed transcription only surfaces a toast from the
 * hook. Rendered only while Clinical Facts is open, so the button never appears
 * without a visible place for its text to land.
 *
 * @param {object}   apiClient - provides `transcribeAudio(dataUrl)`.
 * @param {Function} onText    - called once per dictation with the transcript.
 * @param {boolean}  disabled  - dim + block the button (e.g. no credits).
 */
const DictationButton = ({
  apiClient,
  onText,
  disabled = false,
  id = 'dictation-button',
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [micDevices, setMicDevices] = useState([]);
  const [selectedMicId, setSelectedMicId] = useState(readStoredMicId);

  const refreshMicDevices = useCallback(async () => {
    if (!navigator.mediaDevices?.enumerateDevices) {
      return;
    }
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      setMicDevices(
        devices
          .filter((d) => d.kind === 'audioinput')
          .map((device, index) => ({
            deviceId: device.deviceId,
            label: (device.label && device.label.trim()) ||
              (device.deviceId === 'default'
                ? 'System default microphone'
                : `Microphone ${index + 1}`),
          }))
          .map((d) => ({ ...d, label: d.label.replace(/^Default - /, '') })),
      );
    } catch (err) {
      console.log('Unable to enumerate microphones', err);
    }
  }, []);

  useEffect(() => {
    refreshMicDevices();
  }, [refreshMicDevices]);

  const chooseMic = (deviceId) => {
    setSelectedMicId(deviceId);
    try {
      localStorage.setItem(MIC_STORAGE_KEY, deviceId || '');
    } catch (e) {
      console.log('Unable to persist mic selection', e);
    }
  };

  const { isRecording, isTranscribing, mediaStream, toggle } = useDictation(
    apiClient,
    onText,
    { deviceId: selectedMicId },
  );

  // MicDropStatus animates off a voice-activity tick + a speaking flag, which
  // Recorder.js feeds from its own analyser. Do the same here so the wave on
  // this button behaves identically, without changing that component.
  const [voiceTick, setVoiceTick] = useState(0);
  const [isSpeaking, setIsSpeaking] = useState(false);
  useEffect(() => {
    if (!mediaStream) {
      return undefined;
    }
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    const audioContext = new AudioCtx();
    const source = audioContext.createMediaStreamSource(mediaStream);
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 2048;
    source.connect(analyser);
    const data = new Uint8Array(analyser.fftSize);
    let frames = 0;
    let raf = null;
    let silenceTimer = null;
    const check = () => {
      frames++;
      // Same cadence and threshold as the recorder's detector.
      if (frames % 10 === 0) {
        analyser.getByteTimeDomainData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i++) {
          sum += Math.abs(data[i] - 128);
        }
        if (sum / data.length > 1.2) {
          setVoiceTick((tick) => tick + 1);
          if (silenceTimer) {
            clearTimeout(silenceTimer);
            silenceTimer = null;
          }
          setIsSpeaking(true);
        } else if (!silenceTimer) {
          silenceTimer = setTimeout(() => {
            setIsSpeaking(false);
            silenceTimer = null;
          }, 5000);
        }
      }
      raf = requestAnimationFrame(check);
    };
    check();
    return () => {
      if (raf) {
        cancelAnimationFrame(raf);
      }
      if (silenceTimer) {
        clearTimeout(silenceTimer);
      }
      source.disconnect();
      analyser.disconnect();
      audioContext.close();
    };
  }, [mediaStream]);

  // Transcription is a round trip to the backend; toggling mid-flight would
  // start a second recording whose text could land out of order.
  const isBusy = isTranscribing;
  const isDisabled = disabled || isBusy;

  const defaultMicLabel =
    micDevices.find((d) => d.deviceId === 'default')?.label ||
    'System default microphone';
  const activeMicLabel =
    micDevices.length === 0
      ? 'No microphone found'
      : !selectedMicId || selectedMicId === 'default'
        ? defaultMicLabel
        : micDevices.find((d) => d.deviceId === selectedMicId)?.label ||
          defaultMicLabel;

  // Close the menu on any click outside it, matching the Live Visit button.
  useEffect(() => {
    if (!showDropdown) {
      return undefined;
    }
    function handleClickOutside(event) {
      if (!event.target.closest('.dropdown-container')) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown]);

  // Recording locks the device in; don't let someone switch mid-clip.
  useEffect(() => {
    if (isRecording) {
      setShowDropdown(false);
    }
  }, [isRecording]);

  const title = isRecording
    ? 'Stop dictating and add the text to Additional Visit Information'
    : isBusy
      ? 'Transcribing your dictation…'
      : `Dictate straight into Additional Visit Information (${activeMicLabel})`;

  return (
    <div
      className="dropdown-container cx-dictation-control mic-split-group"
      style={{
        display: 'flex',
        position: 'relative',
        borderRadius: '4px',
      }}
    >
      <Button
        id={id}
        color="primary"
        size="md"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '6px',
          borderRadius: '4px 0 0 4px',
          border: 'none',
          marginRight: '0',
          marginLeft: '0',
          whiteSpace: 'nowrap',
          color: 'white',
          opacity: isDisabled ? 0.6 : 1,
          cursor: isDisabled ? 'not-allowed' : 'pointer',
        }}
        disabled={isDisabled}
        onClick={toggle}
        title={title}
        aria-label={title}
        aria-pressed={isRecording}
      >
        {isRecording ? (
          // Same wave component the record button uses, so the two match while
          // recording. The mic-name line is dropped: the device is locked in
          // for this clip and Stop wants the room.
          <>
            <MicDropStatus
              isConnected={true}
              fromRecorder={true}
              wordChangeCount={voiceTick}
              isSpeaking={isSpeaking}
            />
            <span style={{ fontSize: '14px', fontWeight: 600 }}>Stop</span>
          </>
        ) : (
          <>
            <span
              className={`fa ${
                isBusy ? 'fa-spinner fa-spin' : 'fa-microphone'
              } fa-2x`}
              style={{ flexShrink: 0 }}
            />
            {/* Action + active mic stacked, same two-line face as Live Visit. */}
            <span
              className="mic-label-stack"
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                justifyContent: 'center',
                minWidth: 0,
                lineHeight: 1.05,
                textAlign: 'left',
              }}
            >
              <span
                style={{
                  fontSize: '14px',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                }}
              >
                {isBusy ? 'Transcribing…' : 'Dictation ✨'}
              </span>
              {/* Capped, not sized to the column: the action label sets the
                  width and a long device name ellipsizes instead. */}
              <span
                title={activeMicLabel}
                style={{
                  maxWidth: '110px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontSize: '10px',
                  fontWeight: 400,
                  marginTop: '1px',
                }}
              >
                {micButtonLabel(activeMicLabel)}
              </span>
            </span>
          </>
        )}
      </Button>
      {!isRecording && (
        <Button
          className="cx-dictation-chevron"
          color="primary"
          size="med"
          style={{
            borderRadius: '0 4px 4px 0',
            padding: '0 8px',
            minWidth: 'auto',
            marginLeft: '0',
            border: 'none',
            borderLeft: '1px solid rgba(255,255,255,0.2)',
          }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            refreshMicDevices();
            setShowDropdown((open) => !open);
          }}
          disabled={isDisabled}
          title="Choose dictation microphone"
          aria-label="Choose dictation microphone"
          aria-expanded={showDropdown}
        >
          <span className="fa fa-chevron-down" />
        </Button>
      )}
      {showDropdown && !isRecording && (
        <div
          className="dropdown-container"
          style={{
            position: 'absolute',
            top: '100%',
            right: '0',
            minWidth: '300px',
            backgroundColor: 'white',
            border: '1px solid #ccc',
            borderRadius: '4px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            zIndex: 1000,
            marginTop: '2px',
            padding: '8px 12px',
          }}
        >
          <div
            style={{ fontSize: '11px', color: '#666', fontWeight: 'bold' }}
          >
            Select Microphone
          </div>
          <select
            value={selectedMicId}
            onChange={(e) => chooseMic(e.target.value)}
            onFocus={refreshMicDevices}
            disabled={disabled}
            style={{
              marginTop: '4px',
              padding: '6px 8px',
              borderRadius: '6px',
              border: '1px solid #ccc',
              fontSize: '14px',
            }}
          >
            <option value="">{defaultMicLabel}</option>
            {micDevices
              .filter((d) => d.deviceId !== 'default')
              .map((device) => (
                <option key={device.deviceId} value={device.deviceId}>
                  {device.label}
                </option>
              ))}
          </select>
          <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>
            Shared with the live visit recording.
          </div>
        </div>
      )}
    </div>
  );
};

export default DictationButton;
